// @ts-ignore
import { UTCDate } from '@date-fns/utc';

export const formatDateToISODate = (date: Date): string => {
  const day = date.getUTCDate();
  const month = date.getUTCMonth() + 1;
  const year = date.getUTCFullYear();
  return `${year}-${month < 10 ? '0' : ''}${month}-${day < 10 ? '0' : ''}${day}`;
};

export const formatTimestamp = (isoString: string) => {
  const date = new UTCDate(isoString);

  const pad = (number: number) => (number < 10 ? '0' : '') + number;

  const hours = pad(date.getUTCHours());
  const minutes = pad(date.getUTCMinutes());
  const seconds = pad(date.getUTCSeconds());

  return `${formatDate(date)} ${hours}:${minutes}:${seconds}`;
};

export const formatDate = (date: Date) => {
  const pad = (number: number) => (number < 10 ? '0' : '') + number;
  const day = pad(date.getDate());
  const month = pad(date.getMonth() + 1); // Months are zero-indexed, so we add 1
  const year = date.getFullYear();
  return `${day}/${month}/${year}`;
};

export const parseDate = (str: string): Date | null => {
  if (!str) return null;

  const parts = str.split('/');
  if (parts.length !== 3) return null;

  const [dayStr, monthStr, yearStr] = parts;
  const day = parseInt(dayStr, 10);
  const month = parseInt(monthStr, 10) - 1; // JS months are 0-indexed
  const year = parseInt(yearStr, 10);

  // basic sanity check
  if (isNaN(day) || isNaN(month) || isNaN(year)) return null;

  const date = new Date(year, month, day);
  return isNaN(date.getTime()) ? null : date;
};

export const getCurrentAndThirtyDaysAgoDate = () => {
  const now = new UTCDate();
  const thirtyDaysAgo = new UTCDate();
  thirtyDaysAgo.setUTCDate(now.getUTCDate() - 30);

  return {
    now,
    thirtyDaysAgo
  };
};

export const getDateRangeFromSearchParams = (
  searchParams: URLSearchParams
): { from: Date; to: Date } => {
  const fromDate = new UTCDate(searchParams.get('from') as string);
  const toDate = new UTCDate(searchParams.get('to') as string);
  if (isNaN(fromDate.getTime()) || isNaN(toDate.getTime()) || validateDateRange(fromDate, toDate)) {
    const { now, thirtyDaysAgo } = getCurrentAndThirtyDaysAgoDate();
    return { from: thirtyDaysAgo, to: now };
  }
  return { from: fromDate, to: toDate };
};

export const validateDateRange = (startDate: Date, endDate: Date): string | null => {
  if (startDate > endDate) {
    return 'End date should be after start date UTC';
  }
  if (endDate > new UTCDate()) {
    return 'End date should be before today UTC';
  }
  const MILLISECONDS_PER_DAY = 1000 * 60 * 60 * 24;
  const differenceInMilliseconds = new UTCDate().getTime() - startDate.getTime();
  const differenceInDays = Math.floor(differenceInMilliseconds / MILLISECONDS_PER_DAY);

  if (differenceInDays > 90) {
    return 'Date range should not exceed 90 days from today';
  }
  return null;
};

export const todayUTC = () => {
  const now = new UTCDate();
  return new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate()));
};

export const addDaysUTC = (d: Date, days: number) => {
  const nd = new Date(d.getTime());
  nd.setUTCDate(nd.getUTCDate() + days);
  return nd;
};

export const clamp = (d: Date, min: Date, max: Date) => (d < min ? min : d > max ? max : d);

export const DAY_FMT = 'yyyy-MM-dd'; // for readability; we’ll format manually

export const toYMD = (d: Date) =>
  `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(
    2,
    '0'
  )}`;

export const parseYMD = (str: string): Date => {
  const [year, month, day] = str.split('-').map(Number);
  return new Date(year, month - 1, day);
};
